import { Brick, BrickSim } from "./brickSim";
import { drawIsoBrick } from "./isoBrick";

export type ThemeKey = "toy" | "night" | "blueprint";

interface BrickTheme {
  u: number;
  colors: string[];
  outline?: string;
  rate: number;
}

export const BRICK_THEMES: Record<ThemeKey, BrickTheme> = {
  toy: { u: 26, colors: ["#d8402f", "#f0b72f", "#2f5fd0", "#2f9a55", "#2a2724"], rate: 0.9 },
  night: { u: 24, colors: ["#e98b8b", "#e7c27a", "#7cc6c4", "#a99be0"], rate: 1.1 },
  blueprint: { u: 22, colors: ["#ffffff"], outline: "#1f4fa0", rate: 1 },
};

/**
 * Isometric bricks stack up from the bottom until they fill the canvas, then
 * fade away and start over. Bricks can be grabbed, dragged and thrown.
 * Returns a cleanup function.
 */
export function startBricks(cv: HTMLCanvasElement, themeKey: ThemeKey): () => void {
  const t = BRICK_THEMES[themeKey];
  const ctx = cv.getContext("2d");
  if (!ctx) return () => {};

  const sim = new BrickSim({ width: cv.clientWidth, height: cv.clientHeight, unit: t.u, colors: t.colors, rate: t.rate });

  const resize = () => {
    const dpr = window.devicePixelRatio || 1;
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr;
    cv.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    sim.resize(W, H);
    cv.style.cursor = "grab";
  };

  const drawBrick = (b: Brick, alpha: number, lift = false) => {
    ctx.globalAlpha = alpha;
    drawIsoBrick(ctx, { x: sim.xOf(b), y: b.y, len: b.w * sim.u, color: b.color }, sim.u, { outline: t.outline, lift });
  };

  const draw = () => {
    ctx.clearRect(0, 0, sim.W, sim.H);
    sim.order().forEach((b) => drawBrick(b, sim.fade));
    if (sim.drag) drawBrick(sim.drag.b, 1, true);
    ctx.globalAlpha = 1;
  };

  const pt = (e: PointerEvent) => {
    const r = cv.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * sim.W) / r.width, y: ((e.clientY - r.top) * sim.H) / r.height };
  };

  const onDown = (e: PointerEvent) => {
    if (!sim.grab(pt(e), performance.now())) return;
    cv.setPointerCapture(e.pointerId);
    cv.style.cursor = "grabbing";
  };

  const onMove = (e: PointerEvent) => {
    if (sim.drag) sim.moveDrag(pt(e), performance.now());
  };

  const onDrop = () => {
    if (!sim.drag) return;
    sim.release(performance.now());
    cv.style.cursor = "grab";
  };

  cv.addEventListener("pointerdown", onDown);
  cv.addEventListener("pointermove", onMove);
  cv.addEventListener("pointerup", onDrop);
  cv.addEventListener("pointercancel", onDrop);

  resize();
  let w0 = cv.clientWidth, h0 = cv.clientHeight;
  const ro = new ResizeObserver(() => {
    if (cv.clientWidth !== w0 || cv.clientHeight !== h0) {
      w0 = cv.clientWidth;
      h0 = cv.clientHeight;
      resize();
    }
  });
  ro.observe(cv);

  let raf = 0;
  let last = performance.now();
  const loop = (now: number) => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (sim.ready) {
      sim.step(dt);
      draw();
    }
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);

  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    cv.removeEventListener("pointerdown", onDown);
    cv.removeEventListener("pointermove", onMove);
    cv.removeEventListener("pointerup", onDrop);
    cv.removeEventListener("pointercancel", onDrop);
  };
}
